import { Select } from '../../design/Select';
import { Field, ToggleRow } from './primitives';

export interface AutoUpdate {
  enabled: boolean;
  create_backup: boolean;
  restart: boolean;
  backup_target: 'hdd' | 'cloud';
  window?: { from: number; to: number };
  last_result?: any;
}

export const DEFAULT_AUTO_UPDATE: AutoUpdate = { enabled: false, create_backup: true, restart: true, backup_target: 'hdd' };

/** Was „Auto-Update" für den jeweiligen Server-Typ konkret bedeutet. */
const autoUpdateHint = (type?: string): string => {
  const t = String(type || 'vanilla').toLowerCase();
  if (t === 'bedrock') return 'Prüft alle 6 Std. auf eine neue Bedrock-Version und installiert sie (Welt & Konfiguration bleiben erhalten).';
  if (t.startsWith('steam')) return 'Aktualisiert einmal täglich (nachts) das Spiel UND die installierten Mods (Steam Workshop & Thunderstore).';
  if (t === 'modpack') return 'Prüft alle 6 Std. auf eine neue Modpack-Version (Modrinth & CurseForge) und installiert sie automatisch.';
  if (t === 'paper' || t === 'purpur') return 'Prüft alle 6 Std. auf eine neuere Minecraft-Version und installiert sie automatisch.';
  if (t === 'fabric') return 'Prüft alle 6 Std. auf ein neues Minecraft-Release und aktualisiert Fabric gleich mit.';
  return 'Prüft alle 6 Std. auf ein neues Minecraft-Release und tauscht die Server-Version automatisch.';
};

export const AutoUpdateFields = ({ value, onChange, serverType }: { value: AutoUpdate; onChange: (v: AutoUpdate) => void; serverType?: string }) => {
  const set = (patch: Partial<AutoUpdate>) => onChange({ ...value, ...patch });
  const windowValue = value.window ? `${value.window.from}-${value.window.to}` : 'any';

  return (
    <>
      <ToggleRow label="Automatisch aktualisieren" hint={autoUpdateHint(serverType)} value={value.enabled} onChange={(v) => set({ enabled: v })} />
      {value.enabled && (
        <div className="space-y-5 pt-5 border-t border-line">
          <ToggleRow label="Vorher ein Backup machen" hint="Geht das Update schief, kommt der alte Stand zurück." value={value.create_backup} onChange={(v) => set({ create_backup: v })} />
          <ToggleRow label="Danach neu starten" hint="Nur wenn der Server vorher lief." value={value.restart} onChange={(v) => set({ restart: v })} />
          <div className="grid sm:grid-cols-2 gap-5">
            {value.create_backup && (
              <Field label="Backup-Ziel" hint="Nur HDD-Backups erlauben das automatische Zurückrollen.">
                <Select
                  value={value.backup_target === 'cloud' ? 'cloud' : 'hdd'}
                  onChange={(v) => set({ backup_target: v as 'hdd' | 'cloud' })}
                  options={[
                    { value: 'hdd', label: 'HDD (empfohlen)', hint: 'schnell, mit automatischem Rollback' },
                    { value: 'cloud', label: 'Cloud (Google Drive)', hint: 'langsamer, kein automatisches Rollback' },
                  ]}
                />
              </Field>
            )}
            <Field label="Zeitfenster" hint="Damit niemand mitten am Abend vom Neustart überrascht wird.">
              <Select
                value={windowValue}
                onChange={(v) => {
                  if (v === 'any') return set({ window: undefined });
                  const [from, to] = v.split('-').map((n) => parseInt(n, 10));
                  set({ window: { from: from!, to: to! } });
                }}
                options={[
                  { value: 'any', label: 'Jederzeit', hint: 'Steam-Server prüfen weiterhin nachts 3–6 Uhr' },
                  { value: '0-6', label: 'Nachts 0–6 Uhr' },
                  { value: '3-6', label: 'Nachts 3–6 Uhr' },
                  { value: '4-8', label: 'Früh 4–8 Uhr' },
                ]}
              />
            </Field>
          </div>
        </div>
      )}
    </>
  );
};
